import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  LabelsResponseSchema,
  PullRequestLabelSchema,
  type LabelsResponse,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/labels'
import type {LabelPickerLabel} from '@github-ui/item-picker-label-tsq'

import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import {pullRequestsOverviewRoute} from '../routes/overview-route'

export type MutationData = {
  addLabels: string[]
  removeLabels: string[]
  nextLabels: LabelPickerLabel[]
}

type Callbacks = {
  onError?: (error: Error) => void
}

export function useModifyLabels({onError}: Callbacks = {}) {
  const labelsQueryKey = useRouteQueryKey(pullRequestsOverviewRoute, 'labels')
  const apiURL = usePageDataUrl(PageData.modifyLabels)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const response = await fetchWithErrorHandling(apiURL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: {addLabels: data.addLabels, removeLabels: data.removeLabels},
      })
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
    onMutate: async ({nextLabels}) => {
      if (!labelsQueryKey) return

      await queryClient.cancelQueries({queryKey: labelsQueryKey})
      const previous = queryClient.getQueryData<LabelsResponse>(labelsQueryKey)

      queryClient.setQueryData<LabelsResponse>(labelsQueryKey, () =>
        create(LabelsResponseSchema, {
          labels: nextLabels.map((label, index) =>
            create(PullRequestLabelSchema, {
              id: BigInt(-(index + 1)),
              name: label.name,
              color: label.color,
              description: label.description ?? '',
            }),
          ),
        }),
      )

      return {previous}
    },
    onError: (e: Error, _vars, context) => {
      if (labelsQueryKey && context?.previous) {
        queryClient.setQueryData(labelsQueryKey, context.previous)
      }
      onError?.(e)
    },
    onSettled: () => {
      if (!labelsQueryKey) return
      return queryClient.invalidateQueries({queryKey: labelsQueryKey}, {cancelRefetch: false})
    },
  })
}
